import { Browser } from "../../core/browser";
import { default as Button } from "../button/button";
import { AuxiliumComponent } from "../auxilium-component";

export default class Dropdown extends AuxiliumComponent {
  static observable =
    [
      "name", "value", "icon-colour", "text", "icon", "size", "suffix-icon", "description",
      "label", "secondary-label", "default", "helper-text", "orientation", "searchable-label",
      "hidden-id", "hidden-name", "dropdown-style", "variation", "button-id", "empty-search-title",
      "empty-search-description", "no-search-title", "no-search-description",
      "search-bar-placeholder"
    ];

  static observableBooleans = ["limit-height", "searchable", "required", "static", "select-on-arrow", "local-search", "open", "readonly", "multi"];

  #GetInitialItems = null;
  #GetSearchedItems = null;

  #stopReset = false;
  #stopSearch = false;

  #searchTimeout = 500;

  #preventClose = false;
  #invalid = false;

  #previousValue = "";

  constructor() {
    super();

    this.initialValue = this.value;
    this.MultiSeparator = "|";

    this.selectedItem = null;

    if (this.closest("form") && !this.hiddenName) {
      this.hiddenName = this.id;
    }

    this.selectedText = "";

    this.classList.add("dropdown__wrapper");
  }

  get GetInitialItems() {
    return this.#GetInitialItems;
  }

  set GetInitialItems(val) {
    this.#GetInitialItems = val;

    if (this.localSearch) {
      Promise.resolve(val?.() ?? []).then(items => {
        this.#GetInitialItems = () => items;

        this.GetSearchedItems = value => SearchArray(items, value, "textContent", 0);
        this.#searchTimeout = 0;

        this.#ResetInitialSearch(true);
      });
    } else {
      this.#ResetInitialSearch(true);
    }
  }

  get GetSearchedItems() {
    return this.#GetSearchedItems;
  }

  set GetSearchedItems(val) {
    this.#GetSearchedItems = val;
    this.#searchTimeout = 500;
  }

  parsedCallback() {
    if (this.multi && !this.value) { this.text = "None selected"; }

    if (!this.static) {
      const parentComparer = this.closest("chip-dropdownbutton, chip-splitbutton");

      this.static = ["CHIP-DROPDOWNITEM", "CHIP-PREFIXDROPDOWN", "CHIP-SUFFIXDROPDOWN"].includes(this.parentElement?.nodeName)
        || parentComparer?.querySelector("chip-dropdown") === this;
    }

    const wrapper = document.createElementWithContents("div", null, { className: this.static ? "" : "input-field" });

    if (this.static) {
      wrapper.classList.add("dropdown__static");
    }

    const button = document.createElementWithContents("button", null, {
      type: "button",
      className: "btn--dropdown-trigger",
      id: this.identifier,
      ariaExpanded: false,
      onclick: this.#OnButtonClick
    });

    if (!this.static && !this.closest("chip-splitbutton")) {
      if (!this.text && !this.value && !this.default && this.parentElement?.nodeName !== "CHIP-INPUT") {
        this.value = this.querySelector("chip-dropdownitem")?.value;
      }

      let buttonContent = this.text;
      if (!buttonContent || this.value) { buttonContent = this.querySelector(`chip-dropdownitem[value='${this.value}']`)?.textContent.trim() ?? (this.default || this.text); }

      button.appendChild(document.createElementWithContents("span", buttonContent || "&nbsp;"));
    }

    if (this.description && !button.textContent?.trim() && !document.querySelector(`[for='${this.identifier}']`)) { button.ariaLabel = this.description; }

    if (this.icon) { button.prepend(document.createElementWithContents("chip-icon", null, { icon: this.icon, className: "dropdown--dropdown-icon" })); }
    if (this.size) { button.classList.add(`btn--${this.size.toLowerCase()}`); }

    let items = [...this.children].map(x => x.cloneNode(true));

    if (this.children.length) {
      this.#GetInitialItems = () => items;

      if (!this.GetSearchedItems) {
        this.GetSearchedItems = value => SearchArray(items, value, "textContent", 0);
        this.#searchTimeout = 0;
      }
    }

    if (this.label || this.secondaryLabel) {
      const label = document.createElementWithContents("label", this.label, {
        htmlFor: this.identifier,
        className: "input-field__label"
      });

      if (this.secondaryLabel) { label.appendChild(document.createElementWithContents("span", this.secondaryLabel, { className: "input-field__label--secondary" })); }

      this.appendChild(label);
    }

    if (this.hiddenId || this.hiddenName) {
      const hiddenInput = document.createElementWithContents("input", null, {
        type: "hidden",
        value: this.value
      });

      if (this.hiddenId) { hiddenInput.id = this.hiddenId; }
      if (this.hiddenName) { hiddenInput.name = this.hiddenName; }

      wrapper.appendChild(hiddenInput);
    }

    if (!["CHIP-INPUT", "CHIP-DROPDOWNITEM"].includes(this.parentElement?.nodeName)) {
      wrapper.appendChild(button);
    }

    const list = document.createElementWithContents("ul", null, {
      className: "dropdown__list"
    });

    if (this.limitHeight) { list.classList.add("limit-height"); }

    list.setAttribute("aria-labelledby", this.identifier);
    list.onclick = ev => ev.halt();

    this.#InitializeItemSet([...this.querySelectorAll(":scope > chip-dropdownitem, :scope > chip-dropdowndivider, :scope > chip-dropdowngroup, :scope > chip-dropdownheader")], list);

    let keys = "",
      timeout;

    if (this.searchable) {
      this.#BuildSearchableDropdown(list);
    }

    wrapper.appendChild(list);
    this.appendChild(wrapper);

    if (this.helperText) {
      const helperTextMethod = Browser.GetFlavour() === "gov" ? "beforebegin" : "afterend";

      wrapper.insertAdjacentElement(helperTextMethod, document.createElementWithContents("div", this.helperText, {
        className: "input-field__helper-text"
      }));
    }

    if (this.closest("chip-dropdownbutton")?.querySelector("chip-dropdown") === this) {
      this.#InitDropdownButton();
    }

    this.onkeydown = ev => {
      if (this.readonly) { return; }
      if (!["Enter", "Tab", "Shift"].includes(ev.key) && !this.classList.contains("dropdown--open")) { this.Open(); }

      switch (ev.key) {
        case "Escape":
          this.Close();
          this.closest("chip-input")?.focus();

          keys = "";
          break;
        case "ArrowDown":
          this.ArrowDown(ev);
          break;
        case "ArrowUp":
          this.ArrowUp(ev);
          break;
        default:
          if (timeout) {
            clearTimeout(timeout);
          }

          timeout = setTimeout(() => keys = "", 1000);

          keys += ev.key.toLowerCase();

          [...list.querySelectorAll("chip-dropdownitem")].find(x => x.textContent.trim().toLowerCase().indexOf(keys) === 0)?.parentElement.focus();

          if (ev.code === "Space") {
            ev.preventDefault();
          }
          break;
      }
    }
  }

  ArrowDown(ev) {
    ev.preventDefault();
    ev.stopPropagation();

    const items = [...this.querySelectorAll(".dropdown__list > li")].filter(x => !!x.querySelector("chip-dropdownitem")),
      currentIndex = items.indexOf(document.activeElement?.closest("li")),
      item = (items.at(currentIndex + 1) ?? items.at(0)).querySelector(":scope > button, :scope > a");

    if (item && this.selectOnArrow) {
      this.#SelectItem(item, true);
    }

    item?.focus();
  }

  ArrowUp(ev) {
    ev.preventDefault();
    ev.stopPropagation();

    const items = [...this.querySelectorAll(".dropdown__list > li")].filter(x => !!x.querySelector("chip-dropdownitem")),
      currentIndex = items.indexOf(document.activeElement?.closest("li")),
      item = items.at(currentIndex - 1)?.querySelector(":scope > button, :scope > a");

    if (item && this.selectOnArrow) {
      this.#SelectItem(item, true);
    }

    item?.focus();
  }

  #InitializeItemSet(set, list) {
    list ??= this.querySelector(".dropdown__list");

    if (list) {
      list.querySelectorAll(":scope > li:not(.dropdown--static-search)").forEach(x => x.remove());

      const groups = GroupBy(set, x => x.group != null ? x.group : "None"),
        keys = Object.keys(groups).sort(SortCaseInsensitive);

      if (keys.find(x => x !== "None") != null) {
        // Move the ungrouped section to the end
        if (keys.includes("None")) { keys.splice(keys.length - 1, 0, ...keys.splice(keys.indexOf("None"), 1)); }

        for (const key of keys) {
          if (key) {
            this.AddItem(document.createElementWithContents("chip-dropdowngroup", null, { label: key }), list);
          }

          for (const item of groups[key]) {
            this.AddItem(item, list);
          }
        }
      } else {
        for (const item of set) {
          this.AddItem(item, list);
        }
      }

      this.Reposition(list);
    }
  }

  #BuildDropdownButton(item) {
    let element = document.createElement("div"),
      hasDropdown = !!item.querySelector("chip-dropdown");

    if (!item.detached) {
      if (item.link) {
        element = document.createElementWithContents("a", null, { href: Browser.ResolveRelativeURL(item.link) });
      } else {
        element = !hasDropdown
          ? document.createElementWithContents("button", null, { type: "button" })
          : document.createElementWithContents("span");
      }

      if (item.link && item.download) { element.download = item.download; }
    }

    element.classList.add("dropdown-item--interactable");

    if (item.disabled) { element.setAttribute("disabled", ""); }
    if (item.target) { element.target = item.target; }
    if (item.identifier) { element.id = item.identifier; }

    element.appendChild(item);

    return element;
  }

  AddItem(item, list) {
    list ??= this.querySelector(".dropdown__list");

    if (list) {
      let listItem = document.createElement("li");

      switch (item.nodeName) {
        case "CHIP-DROPDOWNITEM":
          {
            let element = this.#BuildDropdownButton(item);
            listItem.appendChild(element);

            if (!item.detached) {
              const nestedDropdown = element.querySelector("chip-dropdown");

              if (!this.static && !this.multi && item.getAttribute("value") === this.value && this.value != null) {
                this.#SelectItem(element);
              }

              element.onmouseover = ev => {
                ev.stopPropagation();

                [...element.closest("chip-dropdown").querySelectorAll("chip-dropdown.dropdown--open")].filter(x => !x.contains(element)).forEach(x => x.Close());

                nestedDropdown?.Open();
              }

              if (!nestedDropdown) {
                element.onclick = ev => {
                  ev.stopPropagation();

                  if (this.static && !ev.target?.closest("chip-dropdownitem")) {
                    element.querySelector("chip-dropdownitem")?.raiseEvent("click");
                  }

                  if (!this.readonly) {
                    if (this.value === item.getAttribute("value") || !item.hasAttribute("value")) {
                      this.raiseEvent("selectionchange");
                    }

                    if (!this.multi) {
                      this.value = item.getAttribute("value") ?? "";

                      element.closest("chip-dropdown:not(chip-dropdownitem>chip-dropdown)")?.Close();
                    } else {
                      let values = this.value.split(this.MultiSeparator),
                        newValue = item.getAttribute("value");

                      if (values.includes(newValue)) { values = values.filter(x => x !== newValue); }
                      else { values.push(newValue); }

                      this.value = values.join(this.MultiSeparator);
                    }
                  }
                }
              }
            }
            break;
          }
        case "CHIP-DROPDOWNHEADER":
        case "CHIP-DROPDOWNDIVIDER":
          listItem.appendChild(item);
          break;
        case "CHIP-DROPDOWNGROUP":
          listItem.classList.add("dropdown__group-header");
          listItem.appendChild(item);
          break;
      }

      list.appendChild(listItem);
    }
  }

  async #ResetInitialSearch(showPrompt, list) {
    list ??= this.querySelector(".dropdown__list");

    if (list) {
      list.querySelectorAll(":scope > li:not(.dropdown--static-search)").forEach(x => x.remove());
      list.querySelector(".dropdown--searchable__prompt--initial")?.remove();

      list.querySelector(".pv-loading-container")?.classList.remove("d-none");
      list.querySelectorAll("chip-emptyprompt").forEach(x => x.classList.add("d-none"));

      const initialResults = this.GetInitialItems ? await this.GetInitialItems() : [];

      list.querySelector(".pv-loading-container")?.classList.add("d-none");

      if (this.#stopReset) {
        this.#stopReset = false;
        return;
      }

      if (initialResults.length) {
        this.#InitializeItemSet(initialResults, list);

        if (this.value) {
          this.#SelectValue(this.value, true);
        }
      } else if (showPrompt) {
        list.querySelector(".dropdown--searchable__prompt--empty")?.remove();
        this.#AddNoSearchPrompt(list);
      }
    }
  }

  #CancelReset() {
    this.#stopReset = true;
    this.#stopSearch = false;
  }

  #CancelSearch() {
    this.#stopSearch = true;
    this.#stopReset = false;
  }

  RefreshItems(list) {
    list ??= this.querySelector(".dropdown__list");

    if (list) {
      this.#CancelSearch();
      this.#ResetInitialSearch(true, list);
    }
  }

  #AddEmptySearchPrompt(list) {
    list ??= this.querySelector(".dropdown__list");

    list?.querySelector(".dropdown--searchable__wrapper")?.appendChild(document.createElementWithContents("chip-emptyprompt", Localizer.DROPDOWN_EMPTY_SEARCH_DESCRIPTION, {
      size: "sm",
      heading: Localizer.DROPDOWN_EMPTY_SEARCH_TITLE,
      icon: "fas fa-file-alt",
      className: "dropdown--searchable__prompt--empty"
    }));
  }

  #AddNoSearchPrompt(list) {
    list ??= this.querySelector(".dropdown__list");

    list?.querySelector(".dropdown--searchable__wrapper")?.appendChild(document.createElementWithContents("chip-emptyprompt", Localizer.DROPDOWN_NO_SEARCH_DESCRIPTION, {
      size: "sm",
      heading: Localizer.DROPDOWN_NO_SEARCH_DESCRIPTION,
      icon: "far fa-search",
      className: "dropdown--searchable__prompt--initial"
    }));
  }

  async Search(value, list) {
    list ??= this.querySelector(".dropdown__list");

    if (list) {
      list.querySelectorAll(":scope > li:not(.dropdown--static-search)").forEach(x => x.remove());

      if (value.trim() !== "") {
        list.querySelector(".dropdown--searchable__prompt--initial")?.remove();
        this.#CancelReset();

        list.querySelector(".pv-loading-container")?.classList.remove("d-none");
        list.querySelectorAll("chip-emptyprompt").forEach(x => x.classList.add("d-none"));

        const results = this.GetSearchedItems ? await this.GetSearchedItems(value) : [];

        list.querySelector(".pv-loading-container")?.classList.add("d-none");

        if (this.#stopSearch) {
          this.#stopSearch = false;
          return;
        }

        this.#InitializeItemSet(results, list);

        if (this.value && this.#GetItemFromValue(this.value)) {
          this.#SelectValue(this.value, true);
        }

        list.querySelector(".dropdown--searchable__prompt--empty")?.remove();

        if (!results.length) {
          this.#ResetInitialSearch(false, list);

          if (list.children.length === 1) {
            this.#AddEmptySearchPrompt(list);
          }
        }
      } else {
        this.#AddNoSearchPrompt(list);
        this.RefreshItems(list);
      }
    } else {
      this.addEventListener("parsed", () => {
        this.Search(value, list);
      });
    }
  }

  #BuildSearchableDropdown(list) {
    let timeout;

    list.prepend(document.createElementWithContents("li",
      `
        <div class="dropdown--searchable__wrapper">
          <chip-input placeholder="${Localizer.DROPDOWN_SEARCH_BAR_PLACEHOLDER}" icon-style="integrated" prefix-icon="far fa-search"></chip-input>

          <div class="pv-loading-container d-none mt-xxl mb-xl"><div class="pv-loading-icon"></div></div>
        </div>
      `.trim(), { className: "dropdown--static-search" }));

    this.#ResetInitialSearch(true, list);

    const input = list.querySelector("chip-input");

    input.onparsed = () => this.Reposition();

    input.onkeydown = ev => {
      ev.stopImmediatePropagation();
      ev.stopPropagation();

      switch (ev.key) {
        case "Enter":
          clearTimeout(timeout);

          this.Search(input.value, list);
          break;
        case "Escape":
          this.Close();
          break;
        case "ArrowDown":
          this.ArrowDown(ev);
          break;
        case "ArrowUp":
          this.ArrowUp(ev);
          break;
      }
    }

    input.oninput = () => {
      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => {
        this.Search(input.value, list);
      }, this.#searchTimeout);
    }
  }

  #InitDropdownButton() {
    const button = this.querySelector(":scope > div > button"),
      dropdownButton = this.closest("chip-dropdownbutton");

    button.append(...[...dropdownButton.childNodes ?? []].filter(x => !x.matches?.("chip-dropdown")));
    button.classList.add("btn--base", Button.ClassFromVariation(dropdownButton.variation));

    if (dropdownButton.buttonStyle) {
      button.classList.add(`btn--${dropdownButton.buttonStyle.toLowerCase()}`);
    }
  }

  #OnButtonClick(event) {
    event.preventDefault();

    const dropdown = event.target?.closest("chip-dropdown");

    if (dropdown.open || !dropdown.querySelector(".dropdown__list > *")) {
      dropdown?.Close();
    } else {
      dropdown?.Open();
    }
  }

  Reposition(list) {
    list ??= this.querySelector(".dropdown__list");

    if (list?.parentElement) {
      list.removeAttribute("style");
      this.classList.add("dropdown--open");

      const containerDimensions = list.parentElement.getBoundingClientRect(),
        parent = getScrollParent(list);

      const comparatorDimensions = document.body !== parent ? parent.getBoundingClientRect() : {
        bottom: window.innerHeight,
        right: window.innerWidth,
        top: 0,
        left: 0
      },
        { height, top, width, left } = list.getBoundingClientRect();

      if (height + top > comparatorDimensions.bottom && comparatorDimensions.top <= containerDimensions.top - height - containerDimensions.height) {
        list.style.top = `calc(-${height}px - ((var(--dropdown-spacing) * 2) + var(--input-border-width)))`;
      }

      if (width + left > comparatorDimensions.right) {
        list.style.left = `-${width - containerDimensions.width}px`;
      }

      const newLeft = list.getBoundingClientRect().left;

      if (!this.open) {
        this.classList.remove("dropdown--open");
      }

      if (newLeft < comparatorDimensions.left) {
        list.style.left = `${parseInt(window.getComputedStyle(list).left) + (Math.abs(newLeft - comparatorDimensions.left))}px`;
      }
    }
  }

  Toggle() {
    this.open ? this.Close() : this.Open();
  }

  Open() {
    this.Reposition();
    this.classList.add("dropdown--open");

    this.querySelector(".dropdown__list chip-input")?.select();
    this.raiseEvent("opened");

    this.open = true;
  }

  Close() {
    const list = this.querySelector(".dropdown__list");

    if (list) {
      this.removeClass("dropdown--open");
      list.removeAttribute("style");
    }

    this.open = false;
  }

  #SelectMultiple(value) {
    let values = Array.from(new Set((this.value?.split(this.MultiSeparator) ?? []).concat(...(value?.split(this.MultiSeparator) ?? [])))),
      selectedCount = 0,
      valueChanged = this.#previousValue?.toString() !== values.join(this.MultiSeparator),
      defaultIcon = value ? "far" : "";

    this.querySelectorAll("chip-dropdownitem").forEach(item => {
      if (values.includes(item.value)) {
        item.icon = "far fa-check";
        selectedCount += 1;
      } else {
        item.icon = defaultIcon;
      }
    });

    Object.assign(this, {
      text: `${selectedCount !== 0 ? selectedCount : "None"} selected`,
      value: values.join(this.MultiSeparator)
    });

    this.raiseEvent("selectionchange");
    if (valueChanged) { this.raiseEvent("change"); }
  }

  #SelectItem(item, preventClose) {
    const isIconPicker = !!this.closest("chip-iconpicker"),
      button = this.querySelector(":scope > div > button > span");

    if (item) {
      this.#preventClose = preventClose;

      const valueChanged = this.#previousValue?.toString() !== item.firstElementChild?.value?.toString();

      this.selectedItem = item.firstElementChild;
      this.selectedText = item.textContent.trim();

      if (item.firstElementChild.icon) { this.icon = item.firstElementChild.icon; }

      this.value = item.firstElementChild?.value?.toString();

      if (this.hiddenId || this.hiddenName) {
        this.querySelector("input[type='hidden']").value = this.value;
      }

      if (!isIconPicker && !this.static && button) {
        const dropdownItem = item.querySelector(":scope > chip-dropdownitem");

        if (dropdownItem?.textContent.length) {
          button.textContent = dropdownItem.textContent.trim();
        } else {
          button.textContent = this.default;
        }
      }

      if (!this.static || isIconPicker) {
        this.querySelector(".active")?.removeClass("active");
        item.classList.add("active");
      }

      this.raiseEvent("selectionchange");
      if (!preventClose) { this.Close(); }
      if (valueChanged) { this.raiseEvent("change"); }
    } else if (!isIconPicker && !this.static && button) {
      button.textContent = this.default;
    }

    if (this.#invalid) {
      this.reportValidity();
      this.closest("chip-form")?.checkValidity();
    }
  }

  attributeChanged(name, oldValue, newValue) {
    switch (name) {
      case "value":
        this.#previousValue = oldValue;

        this.#SelectValue(newValue, this.#preventClose);
        this.#preventClose = false;
        break;
      case "label":
        {
          const label = this.querySelector(".input-field__label");

          if (label) {
            label.textContent = newValue;
          }
        }
        break;
      case "static":
        {
          const container = this.querySelector("div");

          if (!this.IsAttributeValueTrue(newValue)) {
            container.classList.remove("dropdown__static");
            container.classList.add("input-field");

            if (!this.querySelector(":scope > div > button > span")) {
              this.querySelector(":scope > div > button")?.appendChild(document.createElementWithContents("span", "&nbsp"));
            }
          } else {
            container.classList.add("dropdown__static");
            container.classList.remove("input-field");

            this.querySelector(":scope > div > button > span")?.remove();

            this.querySelectorAll(".active").forEach(x => x.removeClass("active"));
          }
        }
        break;
      case "description":
        {
          const button = this.querySelector(":scope > div > button");

          if (button && !button.textContent?.trim() && !document.querySelector(`[for='${button.id}']`)) {
            button.ariaLabel = newValue;
          }
        }
        break;
      case "icon-colour":
        if (!newValue) {
          this.querySelector(".input-field > button > chip-icon").removeAttribute("style");
        } else {
          this.querySelector(".input-field > button > chip-icon").style.color = newValue;
        }
        break;
      case "text":
        this.querySelector(":scope > div > button > span").textContent = newValue;
        break;
      case "size":
        {
          const button = this.querySelector(":scope > div > button");

          button?.classList.remove(`btn--${this.size?.toLowerCase()}`);
          button?.classList.add(`btn--${newValue.toLowerCase()}`);
        }
        break;
      case "icon": {
        const icon = this.querySelector(".dropdown--dropdown-icon");

        if (icon) {
          if (!newValue) {
            icon.remove();
          } else {
            icon.icon = newValue;
          }
        } else if (newValue) {
          this.querySelector(":scope > div > button")?.prepend(document.createElementWithContents("chip-icon", null, { icon: newValue, className: "dropdown--dropdown-icon" }));
        }
      }
        break;
      case "suffix-icon": {
        const icon = this.querySelector(":scope > div > button > chip-icon:last-child:not(:first-child)");

        if (icon) {
          icon.icon = newValue;
        } else {
          this.querySelector(":scope > div > button")?.appendChild(document.createElementWithContents("chip-icon", null, { icon: newValue }));
        }
      }
        break;
    }
  }

  checkValidity() {
    if (this.required && ["", undefined, null].includes(this.value)) {
      this.validationMessage = Localizer.DROPDOWN_REQUIRED;
      return false;
    } else {
      return true;
    }
  }

  setInvalid(message) {
    const msgElement = document.createElementWithContents("div", message, { className: "input-field__validation-error" });
    this.querySelector(".input-field")?.classList.add("input-field__invalid");

    if (Browser.GetFlavour() === "gov") {
      this.querySelector(":scope > div").insertAdjacentElement("beforebegin", msgElement);
    } else {
      this.appendChild(msgElement);
    }
  }

  reportValidity() {
    const valid = this.checkValidity();

    this.querySelector(".input-field__validation-error")?.remove();
    this.querySelector(".input-field")?.classList.remove("input-field__invalid");

    if (!valid) {
      this.setInvalid(this.validationMessage);
    }

    this.#invalid = !valid;

    return valid;
  }

  #GetItemFromValue(value) {
    value ??= "";

    return this.querySelector(`chip-dropdownitem[value='${value}']`)
      ?? [...this.querySelectorAll("chip-dropdownitem")].find(x => x.value?.toString() === value.toString() || x.getAttribute("value")?.toString() === value?.toString());
  }

  SelectText(text) {
    const target = [...this.querySelectorAll("chip-dropdownitem")].find(x => x.textContent?.trim() === text.trim());

    if (target) {
      this.#SelectValue(target.value ?? target.getAttribute("value"));
    }
  }

  #SelectValue(value, preventClose) {
    if (!this.multi) {
      const target = this.#GetItemFromValue(value);

      if (target) {
        this.#SelectItem(target.closest("button, a"), preventClose);
      } else if (!this.static) {
        if (!this.closest("chip-iconpicker")) {
          this.querySelector(".active")?.removeClass("active");
        }

        this.querySelector(":scope > div > button > span").innerHTML = this.default || "&NoBreak;";
      }
    } else {
      this.#SelectMultiple(value);
    }
  }
}
